import type { CSSProperties, ReactNode } from 'react';
import classnames from 'classnames';
import { useDamageChip } from './use-damage-chip';
import styles from './ResourceBar.module.scss';

/**
 * One pass of a highlight across the bar: `durationMs` from start to end,
 * already `elapsedMs` along. A new `key` starts a new pass.
 */
export interface ResourceBarSweep {
    readonly key: string | number;
    readonly durationMs: number;
    readonly elapsedMs: number;
}

interface ResourceBarProps {
    value: number;
    maxValue: number;
    /** Fill color. The essence art is monochrome and takes this color. */
    color?: string;
    /** Text in the label frame. The label frame shows only when this is set. */
    label?: ReactNode;
    /** Whether the label frame sits under the bar (default) or over it. */
    labelPosition?: 'top' | 'bottom';
    /** Accessible name for the meter, e.g. "Essence". */
    ariaLabel?: string;
    /** Shows each drop as a lighter chip that drains away, with the amount taken popping off the fill (#201). */
    isDamageTakenShown?: boolean;
    /** A highlight sweeping the bar from start to end (Draw's pulse), or none. */
    sweep?: ResourceBarSweep;
    className?: string;
}

/**
 * Framed essence bar (e.g. a boss's essence). The frame and the label are
 * three-column grids: caps sized by `--resource-bar-height` around a center
 * that stretches. The frame fills `--resource-bar-width`; the label fits its
 * text and overlaps the bar's bottom (or top) edge by 30% of its height. The
 * fill sits behind the frame's open channel. The fill and the cap gems are
 * tinted to `color`; the frame and label to `--resource-bar-frame-color`.
 * With `isDamageTakenShown`, a drop shows at once, the lost span stays as a
 * lighter chip for a moment and drains away, and the amount taken
 * (summed over quick hits) pops off the fill's end. A `sweep` runs a soft
 * light across the bar from start to end, once a pass.
 */
export function ResourceBar({
    value,
    maxValue,
    color,
    label,
    labelPosition = 'bottom',
    ariaLabel,
    isDamageTakenShown = false,
    sweep,
    className,
}: ResourceBarProps): JSX.Element {
    const toRatio = (amount: number): number => (maxValue > 0 ? Math.min(1, Math.max(0, amount / maxValue)) : 0);
    const fillRatio = toRatio(value);
    const chip = useDamageChip(value, isDamageTakenShown);
    const chipRatio = chip.fromValue === null || chip.draining ? fillRatio : toRatio(chip.fromValue);

    return (
        <div
            className={classnames(styles.resourceBar, labelPosition === 'top' && styles.labelTop, isDamageTakenShown && styles.showsDamageTaken, className)}
            style={{ '--bar-color': color, '--fill-ratio': fillRatio, '--chip-ratio': chipRatio } as CSSProperties}
            role="meter"
            aria-label={ariaLabel}
            aria-valuemin={0}
            aria-valuemax={maxValue}
            aria-valuenow={value}
        >
            <div className={styles.bar}>
                <div className={styles.frame}>
                    <div className={styles.frameStart} />
                    <div className={styles.frameCenter} />
                    <div className={styles.frameEnd} />
                </div>
                {isDamageTakenShown && <div className={styles.chip} />}
                <div className={styles.fill} />
                {sweep !== undefined && (
                    <div
                        key={sweep.key}
                        className={styles.sweep}
                        style={{ '--sweep-duration': `${sweep.durationMs}ms`, '--sweep-delay': `${-sweep.elapsedMs}ms` } as CSSProperties}
                        aria-hidden="true"
                    />
                )}
                {isDamageTakenShown && chip.fromValue !== null && (
                    <span key={`${chip.id}:${chip.taken}`} className={classnames(styles.taken, chip.draining && styles.takenFading)} aria-hidden="true">
                        {`-${Math.round(chip.taken)}`}
                    </span>
                )}
                <div className={classnames(styles.gem, styles.gemStart)} />
                <div className={classnames(styles.gem, styles.gemEnd)} />
            </div>
            {label !== undefined && (
                <div className={styles.label}>
                    <div className={styles.labelStart} />
                    <div className={styles.labelCenter}>{label}</div>
                    <div className={styles.labelEnd} />
                </div>
            )}
        </div>
    );
}
